import { Link } from "react-router-dom";
import { Shield, Mail, Phone, MapPin } from "lucide-react";
import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { EditableText, useContentText } from "../lib/content";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const contactPhone = useContentText("footer.contact.phone", "0800 123 4567");
  const contactPhoneDigits = contactPhone.replace(/\D/g, "");
  const contactEmail = useContentText("footer.contact.email", "hello@protectmybusinessname.co.uk");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/leads", { email, source: "newsletter" });
      toast.success("Thanks. You're on the list.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not sign up. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="bg-[#04273d] text-white pt-16 pb-8 mt-24" data-testid="site-footer">
      <div className="px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <a
              href="https://nationalbusinessregister.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0"
              data-testid="footer-logo-link"
              aria-label="Visit the NBR Group website (opens in a new tab)"
            >
              <img
                src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/static/brand/logo-white.png`}
                alt="NBR Business Name Protection"
                className="h-14 w-auto"
                data-testid="footer-logo-image"
              />
            </a>
            <a
              href="https://nationalbusinessregister.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono-brand text-[10px] text-[#9ac7f7] tracking-widest uppercase hover:text-[#71edc9]"
              data-testid="footer-nbr-link"
            >
              A NBR Group Service
            </a>
          </div>
          <p className="mt-5 text-[#9ac7f7] text-sm leading-relaxed max-w-md">
            <EditableText keyName="footer.tagline" default="The UK's only dedicated business name protection service. Affordable, practical and built specifically for UK small businesses. 2000+ already trust us." />
          </p>

          <form onSubmit={subscribe} className="mt-6 flex max-w-md gap-2" data-testid="footer-newsletter-form">
            <div className="relative flex-1">
              <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9ac7f7]" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Your email for our monthly newsletter"
                className="w-full bg-white/5 border border-white/10 text-white placeholder:text-[#9ac7f7]/60 rounded-full pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-[#71edc9]"
                data-testid="footer-newsletter-email-input"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="btn-primary text-sm py-3 px-5"
              data-testid="footer-newsletter-submit-button"
            >
              {busy ? "..." : "Join"}
            </button>
          </form>
          <p className="text-[11px] text-[#9ac7f7]/70 mt-2">
            <EditableText keyName="footer.newsletter.helper" default="Monthly newsletter and regular IP insights. No spam." />
          </p>
        </div>

        <div className="md:col-span-2">
          <p className="font-display font-semibold text-sm mb-3 text-white">Product</p>
          <ul className="space-y-2 text-sm text-[#9ac7f7]">
            <li><Link to="/business-name-protection" className="hover:text-[#71edc9]">Benefits</Link></li>
            <li><Link to="/how-it-works" className="hover:text-[#71edc9]">How it works</Link></li>
            <li><Link to="/pricing" className="hover:text-[#71edc9]">Pricing</Link></li>
            <li><Link to="/check" className="hover:text-[#71edc9]">Free name check</Link></li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="font-display font-semibold text-sm mb-3 text-white">Company</p>
          <ul className="space-y-2 text-sm text-[#9ac7f7]">
            <li><Link to="/about" className="hover:text-[#71edc9]">About</Link></li>
            <li><Link to="/partnerships" className="hover:text-[#71edc9]">Partners</Link></li>
            <li><Link to="/insights" className="hover:text-[#71edc9]">Blogs & Guidance</Link></li>
            <li><Link to="/faqs" className="hover:text-[#71edc9]">FAQs</Link></li>
          </ul>
        </div>
        <div className="md:col-span-3">
          <p className="font-display font-semibold text-sm mb-3 text-white">Contact</p>
          <ul className="space-y-2 text-sm text-[#9ac7f7]">
            <li className="flex items-center gap-2"><Phone size={14} /> <a href={`tel:${contactPhoneDigits}`} className="hover:text-[#71edc9]">{contactPhone}</a></li>
            <li className="flex items-center gap-2"><Mail size={14} /> <a href={`mailto:${contactEmail}`} className="hover:text-[#71edc9]">{contactEmail}</a></li>
            <li className="flex items-start gap-2" data-testid="footer-address">
              <MapPin size={14} className="mt-0.5 shrink-0" />
              <address className="not-italic leading-relaxed">
                National Business Register Group Ltd<br />
                Suite 7, The Courtyard, Russell House,<br />
                6 Doctors Lane, Henley-in-Arden,<br />
                Warwickshire, B95 5AW
              </address>
            </li>
          </ul>
          <p className="font-display font-semibold text-sm mt-5 mb-3 text-white">Legal</p>
          <ul className="space-y-2 text-sm text-[#9ac7f7]">
            <li>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/static/legal/terms-and-conditions.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#71edc9]"
                data-testid="footer-terms-link"
              >Terms and conditions</a>
            </li>
            <li>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/static/legal/privacy-policy.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#71edc9]"
                data-testid="footer-privacy-link"
              >Privacy policy</a>
            </li>
          </ul>
        </div>
      </div>

      <div className="px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3">
        <p className="text-xs text-[#9ac7f7]/70 font-mono-brand">© {new Date().getFullYear()} <a href="https://nationalbusinessregister.co.uk" target="_blank" rel="noopener noreferrer" className="hover:text-[#71edc9] underline-offset-4 hover:underline" data-testid="footer-copyright-nbr-link">NBR Group Ltd</a>. United Kingdom.</p>
        <Link to="/admin/login" className="text-[10px] text-[#9ac7f7]/50 hover:text-[#71edc9] font-mono-brand tracking-widest uppercase" data-testid="footer-admin-link">Admin</Link>
      </div>
    </footer>
  );
}
